/**
 * Core type definitions and utilities for handling unit of measure across the application
 */
import { UnitOfMeasure } from '@prisma/client'
import { IProductVariantExtended } from '../operations/product-variant'

// Re-export UnitOfMeasure for easier imports
export { UnitOfMeasure } from '@prisma/client'

/**
 * Default UnitOfMeasure to use when none is specified
 */
export const DEFAULT_UNIT_OF_MEASURE = UnitOfMeasure.YARD

/**
 * Core Unit of Measure Configuration Interface
 * Base interface for unit of measure settings
 */
export interface IUnitOfMeasureConfig {
  unitOfMeasure: UnitOfMeasure
  minOrderQuantity: number
  maxOrderQuantity?: number | null
  incrementStep: number

  // Roll specific settings
  rollLengthUnits?: number | null
  rollPrice?: number | null

  // Unit-specific pricing
  pricePerUnit: number

  // Settings for display
  displayPrecision: number
  displaySuffix?: string
}

/**
 * Interface for unit conversion factors
 */
export interface IUnitConversionFactor {
  id: string
  fromUnit: UnitOfMeasure
  toUnit: UnitOfMeasure
  factor: number
}

/**
 * Order item unit of measure properties
 */
export interface IOrderItemUoM {
  unitOfMeasure: UnitOfMeasure
  isSoldByRoll: boolean
  rollLength?: number | null
}

/**
 * Format a price with its unit of measure
 * @param price The price value to format
 * @param unitOfMeasure The unit of measure
 * @returns Formatted price string with unit
 */
export function formatPriceWithUnit(price: number, unitOfMeasure: UnitOfMeasure | string): string {
  if (price <= 0) {
    return 'Price unavailable'
  }

  // Handle missing or invalid unit of measure
  const unit = unitOfMeasure || DEFAULT_UNIT_OF_MEASURE

  // Display appropriate unit label
  const unitLabel = getUnitDisplay(unit)

  // Format the price
  return `$${price.toFixed(2)}/${unitLabel}`
}

/**
 * Get a user-friendly display version of a unit of measure
 * @param unit The unit of measure
 * @returns A simplified display string
 */
export function getUnitDisplay(unit: UnitOfMeasure | string): string {
  // Ensure we have a string
  const unitStr = String(unit).toUpperCase()

  // Map certain units to nicer display versions
  const unitDisplayMap: Record<string, string> = {
    METER: 'meter',
    YARD: 'yard',
    FOOT: 'foot',
    INCH: 'inch',
    CENTIMETER: 'cm',
    MILLIMETER: 'mm',
    SQUAREFOOT: 'sq ft',
    SQUAREMETER: 'sq m',
    SQUAREYARD: 'sq yd',
    ROLL: 'roll',
    EACH: 'each',
    IMPERIAL: 'unit',
    METRIC: 'unit'
  }

  return unitDisplayMap[unitStr] || unitStr.toLowerCase()
}

/**
 * Resolves a unit of measure from variant attributes or fallbacks
 * @param variant The variant object
 * @returns The resolved UnitOfMeasure
 */
export function resolveUnitOfMeasure(variant: { unitOfMeasure?: UnitOfMeasure } | null | undefined): UnitOfMeasure {
  // Try to get unitOfMeasure from variant
  if (variant?.unitOfMeasure && Object.values(UnitOfMeasure).includes(variant.unitOfMeasure)) {
    return variant.unitOfMeasure
  }

  // Default to YARD as specified in DEFAULT_UNIT_OF_MEASURE
  return DEFAULT_UNIT_OF_MEASURE
}

/**
 * Resolves the minimum order quantity from variant attributes or fallbacks
 * @param variant The variant object
 * @returns The resolved minimum order quantity
 */
export function resolveMinOrderQuantity(variant: any): number {
  // Try to get from direct variant property
  if (variant?.minOrderQuantity !== undefined && variant.minOrderQuantity !== null) {
    return Number(variant.minOrderQuantity)
  }

  // Try to get from attributeValues
  if (variant?.attributeValues?.MIN_ORDER !== undefined && variant.attributeValues.MIN_ORDER !== null) {
    return Number(variant.attributeValues.MIN_ORDER)
  }

  // Default minimum
  return 1
}

/**
 * Resolves the increment step from variant attributes or fallbacks
 * @param variant The variant object
 * @returns The resolved increment step
 */
export function resolveIncrementStep(variant: any): number {
  // Try to get from direct variant property
  if (variant?.incrementStep !== undefined && variant.incrementStep !== null) {
    return Number(variant.incrementStep)
  }

  // Try to get from attributeValues
  if (variant?.attributeValues?.INCREMENT_STEP !== undefined && variant.attributeValues.INCREMENT_STEP !== null) {
    return Number(variant.attributeValues.INCREMENT_STEP)
  }

  // Default increment
  return 1
}

/**
 * Checks if a variant can be sold as a full roll
 * @param variant The variant object
 * @returns Boolean indicating if it can be sold as a roll
 */
export function canBeSoldAsRoll(variant: any): boolean {
  // Check if roll price and roll length are available
  return (
    ((variant?.rollPrice !== undefined && variant.rollPrice !== null) ||
      (variant?.attributeValues?.ROLL_PRICE !== undefined && variant.attributeValues.ROLL_PRICE !== null)) &&
    ((variant?.rollLengthUnits !== undefined && variant.rollLengthUnits !== null) ||
      (variant?.attributeValues?.ROLL_LENGTH !== undefined && variant.attributeValues.ROLL_LENGTH !== null))
  )
}

/**
 * Validates if a quantity is valid for a given variant
 * @param quantity The quantity to validate
 * @param variant The variant object with constraints
 * @returns Object with validation result and error message
 */
export function validateQuantity(quantity: number, variant: any): { isValid: boolean; errorMessage?: string } {
  const min = resolveMinOrderQuantity(variant)
  const step = resolveIncrementStep(variant)

  // Get max if available
  const max =
    variant?.maxOrderQuantity !== undefined && variant.maxOrderQuantity !== null
      ? Number(variant.maxOrderQuantity)
      : variant?.attributeValues?.MAX_ORDER !== undefined && variant.attributeValues.MAX_ORDER !== null
        ? Number(variant.attributeValues.MAX_ORDER)
        : null

  // Check minimum
  if (quantity < min) {
    return {
      isValid: false,
      errorMessage: `Minimum order quantity is ${min}`
    }
  }

  // Check maximum if it exists
  if (max !== null && quantity > max) {
    return {
      isValid: false,
      errorMessage: `Maximum order quantity is ${max}`
    }
  }

  // Check increment
  if (step > 0 && (quantity - min) % step !== 0) {
    return {
      isValid: false,
      errorMessage: `Quantity must be in increments of ${step} from the minimum of ${min}`
    }
  }

  return { isValid: true }
}

/**
 * Adds or updates the unitOfMeasure property on a selected variant
 * @param variant The variant object to update
 * @returns A new variant object with unitOfMeasure set
 */
export function enrichVariantWithUnitOfMeasure<T extends object>(variant: T): T & { unitOfMeasure: UnitOfMeasure } {
  // Get the unit of measure from the variant
  const unitOfMeasure = resolveUnitOfMeasure(variant)

  // Create a new object with the unitOfMeasure property
  return {
    ...variant,
    unitOfMeasure
  }
}

/**
 * Default configuration values for different unit types
 */
export const defaultUnitConfigs: Record<UnitOfMeasure, Partial<IUnitOfMeasureConfig>> = {
  // Length-based units
  METER: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 0.1,
    displayPrecision: 1,
    displaySuffix: 'm'
  },
  CENTIMETER: {
    minOrderQuantity: 10,
    maxOrderQuantity: 10000,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'cm'
  },
  MILLIMETER: {
    minOrderQuantity: 100,
    maxOrderQuantity: 100000,
    incrementStep: 10,
    displayPrecision: 0,
    displaySuffix: 'mm'
  },
  FOOT: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 0.5,
    displayPrecision: 1,
    displaySuffix: 'ft'
  },
  INCH: {
    minOrderQuantity: 12,
    maxOrderQuantity: 12000,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'in'
  },
  YARD: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 0.5,
    displayPrecision: 1,
    displaySuffix: 'yd'
  },

  // Area-based units
  SQUAREFOOT: {
    minOrderQuantity: 1,
    maxOrderQuantity: 10000,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'sq ft'
  },
  SQUAREMETER: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 0.1,
    displayPrecision: 1,
    displaySuffix: 'sq m'
  },
  SQUAREYARD: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 0.5,
    displayPrecision: 1,
    displaySuffix: 'sq yd'
  },

  // Volume-based units
  CUBIC_FOOT: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'cu ft'
  },
  CUBIC_METER: {
    minOrderQuantity: 1,
    maxOrderQuantity: 100,
    incrementStep: 0.1,
    displayPrecision: 1,
    displaySuffix: 'cu m'
  },
  CUBIC_YARD: {
    minOrderQuantity: 1,
    maxOrderQuantity: 100,
    incrementStep: 0.5,
    displayPrecision: 1,
    displaySuffix: 'cu yd'
  },
  CUBIC_CENTIMETER: {
    minOrderQuantity: 100,
    maxOrderQuantity: 100000,
    incrementStep: 10,
    displayPrecision: 0,
    displaySuffix: 'cc'
  },
  CUBIC_MILLIMETER: {
    minOrderQuantity: 1000,
    maxOrderQuantity: 1000000,
    incrementStep: 100,
    displayPrecision: 0,
    displaySuffix: 'cu mm'
  },
  CUBIC_INCH: {
    minOrderQuantity: 10,
    maxOrderQuantity: 10000,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'cu in'
  },

  // Liquid volume
  LITER: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 0.5,
    displayPrecision: 1,
    displaySuffix: 'L'
  },
  MILLILITER: {
    minOrderQuantity: 100,
    maxOrderQuantity: 100000,
    incrementStep: 10,
    displayPrecision: 0,
    displaySuffix: 'mL'
  },
  GALLON: {
    minOrderQuantity: 1,
    maxOrderQuantity: 100,
    incrementStep: 0.5,
    displayPrecision: 1,
    displaySuffix: 'gal'
  },
  QUART: {
    minOrderQuantity: 1,
    maxOrderQuantity: 400,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'qt'
  },
  PINT: {
    minOrderQuantity: 1,
    maxOrderQuantity: 800,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'pt'
  },
  CUP: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'cup'
  },
  OUNCE: {
    minOrderQuantity: 1,
    maxOrderQuantity: 10000,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'oz'
  },

  // Quantity-based units
  EACH: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: ''
  },
  BOX: {
    minOrderQuantity: 1,
    maxOrderQuantity: 100,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'box'
  },
  CASE: {
    minOrderQuantity: 1,
    maxOrderQuantity: 100,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'case'
  },
  PACK: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'pack'
  },
  PALLET: {
    minOrderQuantity: 1,
    maxOrderQuantity: 10,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'pallet'
  },
  ROLL: {
    minOrderQuantity: 1,
    maxOrderQuantity: 100,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'roll'
  },
  SET: {
    minOrderQuantity: 1,
    maxOrderQuantity: 100,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'set'
  },
  SHEET: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'sheet'
  },
  UNIT: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: 'unit'
  },

  // Imperial catch-all
  IMPERIAL: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 1,
    displayPrecision: 0,
    displaySuffix: ''
  },

  // Metric catch-all
  METRIC: {
    minOrderQuantity: 1,
    maxOrderQuantity: 1000,
    incrementStep: 0.1,
    displayPrecision: 1,
    displaySuffix: ''
  }
}

/**
 * Group units by type for better organization in UI
 */
export const unitGroupings = {
  length: [
    UnitOfMeasure.METER,
    UnitOfMeasure.CENTIMETER,
    UnitOfMeasure.MILLIMETER,
    UnitOfMeasure.FOOT,
    UnitOfMeasure.INCH,
    UnitOfMeasure.YARD
  ],
  area: [UnitOfMeasure.SQUAREFOOT, UnitOfMeasure.SQUAREMETER, UnitOfMeasure.SQUAREYARD],
  volume: [
    UnitOfMeasure.CUBIC_FOOT,
    UnitOfMeasure.CUBIC_METER,
    UnitOfMeasure.CUBIC_YARD,
    UnitOfMeasure.CUBIC_CENTIMETER,
    UnitOfMeasure.CUBIC_MILLIMETER,
    UnitOfMeasure.CUBIC_INCH
  ],
  liquid: [
    UnitOfMeasure.LITER,
    UnitOfMeasure.MILLILITER,
    UnitOfMeasure.GALLON,
    UnitOfMeasure.QUART,
    UnitOfMeasure.PINT,
    UnitOfMeasure.CUP,
    UnitOfMeasure.OUNCE
  ],
  quantity: [
    UnitOfMeasure.EACH,
    UnitOfMeasure.BOX,
    UnitOfMeasure.CASE,
    UnitOfMeasure.PACK,
    UnitOfMeasure.PALLET,
    UnitOfMeasure.ROLL,
    UnitOfMeasure.SET,
    UnitOfMeasure.SHEET,
    UnitOfMeasure.UNIT
  ],
  general: [UnitOfMeasure.IMPERIAL, UnitOfMeasure.METRIC]
}

/**
 * List of units that support roll configuration
 */
const ROLL_SUPPORTING_UNITS = [
  UnitOfMeasure.METER,
  UnitOfMeasure.CENTIMETER,
  UnitOfMeasure.MILLIMETER,
  UnitOfMeasure.FOOT,
  UnitOfMeasure.INCH,
  UnitOfMeasure.YARD,
  UnitOfMeasure.ROLL
] as const

/**
 * Type for units that support roll configuration
 */
type RollSupportingUnit = (typeof ROLL_SUPPORTING_UNITS)[number]

/**
 * Determine if a unit supports roll configuration
 */
export function supportsRollConfiguration(unit: UnitOfMeasure): boolean {
  return ROLL_SUPPORTING_UNITS.includes(unit as any)
}

/**
 * Get a readable display name for a unit of measure
 */
export function getUnitOfMeasureDisplayName(unit: UnitOfMeasure): string {
  const displayNames: Record<UnitOfMeasure, string> = {
    METER: 'Meter',
    CENTIMETER: 'Centimeter',
    MILLIMETER: 'Millimeter',
    FOOT: 'Foot',
    INCH: 'Inch',
    YARD: 'Yard',
    SQUAREFOOT: 'Square Foot',
    SQUAREMETER: 'Square Meter',
    SQUAREYARD: 'Square Yard',
    CUBIC_FOOT: 'Cubic Foot',
    CUBIC_METER: 'Cubic Meter',
    CUBIC_YARD: 'Cubic Yard',
    CUBIC_CENTIMETER: 'Cubic Centimeter',
    CUBIC_MILLIMETER: 'Cubic Millimeter',
    CUBIC_INCH: 'Cubic Inch',
    LITER: 'Liter',
    MILLILITER: 'Milliliter',
    GALLON: 'Gallon',
    QUART: 'Quart',
    PINT: 'Pint',
    CUP: 'Cup',
    OUNCE: 'Ounce',
    EACH: 'Each',
    BOX: 'Box',
    CASE: 'Case',
    PACK: 'Pack',
    PALLET: 'Pallet',
    ROLL: 'Roll',
    SET: 'Set',
    SHEET: 'Sheet',
    UNIT: 'Unit',
    IMPERIAL: 'Imperial',
    METRIC: 'Metric'
  }

  return displayNames[unit] || unit
}
